import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ArrowDownTrayIcon, ChevronDownIcon, ChevronUpIcon, LinkIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import { productsApi, facebookCatalogApi } from '../../utils/api';
import { getDeviseActive } from '../../utils/devise';

/**
 * Fonction pour slugifier une chaîne de caractères
 * @param {string} text - Le texte à slugifier
 * @returns {string} Le texte slugifié
 */
const slugify = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD') // Décompose les caractères accentués
    .replace(/[\u0300-\u036f]/g, '') // Supprime les diacritiques
    .replace(/\s+/g, '-') // Remplace les espaces par des tirets
    .replace(/[^\w-]+/g, '') // Supprime les caractères non alphanumériques
    .replace(/--+/g, '-') // Remplace les tirets multiples par un seul
    .replace(/^-+/, '') // Supprime les tirets au début
    .replace(/-+$/, ''); // Supprime les tirets à la fin
};

/**
 * Génère un slug complet pour l'URL d'un produit
 * Format : {titre-slugifié}-{numeric_id}
 * @param {string} title - Titre du produit
 * @param {number|string} numeric_id - ID numérique du produit
 * @returns {string} Slug complet pour l'URL
 */
const generateProductSlug = (title, numeric_id) => {
  const titleSlug = slugify(title);
  return `${titleSlug}-${numeric_id}`;
};

/**
 * Modal pour exporter les produits au format Catalogue Facebook (CSV).
 * Permet de filtrer par tags, définir l'URL de base et configurer des champs par défaut.
 */
const FacebookExportModal = ({ isOpen, onClose }) => {
  const [shopUrl, setShopUrl] = useState('');
  const [tagsInput, setTagsInput] = useState('');

  // Champs par défaut (Override fields)
  const [overrideFields, setOverrideFields] = useState({
    gender: '',
    age_group: '',
    brand: 'Ma Boutique',
    google_product_category: ''
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // État pour l'URL automatique du catalogue Facebook
  const [feedUrl, setFeedUrl] = useState('');
  const [feedLoading, setFeedLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Charger le token existant à l'ouverture du modal
  useEffect(() => {
    if (!isOpen) return;
    facebookCatalogApi.getTokenInfo()
      .then(res => {
        if (res?.data?.data?.feedUrl) {
          setFeedUrl(res.data.data.feedUrl);
          // Pré-remplir le shopUrl si déjà sauvegardé
          if (res.data.data.shopUrl && !shopUrl) {
            setShopUrl(res.data.data.shopUrl);
          }
          // Pré-remplir les overrides si déjà sauvegardés
          if (res.data.data.overrideFields) {
            setOverrideFields(prev => ({ ...prev, ...res.data.data.overrideFields }));
          }
        }
      })
      .catch(() => {}); // Silencieux si pas encore de token
  }, [isOpen]);

  // Gérer la soumission du formulaire d'export
  const handleExport = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // 1. Préparer les filtres
      const tags = tagsInput
        ? tagsInput.split(',').map(tag => tag.trim()).filter(Boolean)
        : [];

      const params = {};
      if (tags.length > 0) {
        params.tags = tags;
      }

      // 2. Récupérer les produits depuis l'API
      const response = await productsApi.getAll(params);

      if (!response || !response.data) {
        throw new Error("Impossible de récupérer les produits.");
      }

      const products = Array.isArray(response.data) ? response.data : [];

      if (products.length === 0) {
        setError("Aucun produit trouvé avec ces critères.");
        setLoading(false);
        return;
      }

      // Vérifier qu'au moins un produit a des images générées par IA
      const productsWithAiImages = products.filter(p => p.generated_images && p.generated_images.length > 0);

      if (productsWithAiImages.length === 0) {
        setError("Aucun produit avec images générées par IA trouvé. L'export Facebook nécessite au moins une image générée par IA par produit.");
        setLoading(false);
        return;
      }

      // 3. Convertir en CSV Facebook
      const csvContent = convertToFacebookCSV(products, shopUrl, overrideFields);

      // 4. Déclencher le téléchargement
      downloadCSV(csvContent, 'catalog_products.csv');

      setSuccess(`${productsWithAiImages.length} produit(s) avec images IA exporté(s) avec succès !`);

      setTimeout(() => {
        onClose();
        setSuccess(null);
        setError(null);
      }, 2000);

    } catch (err) {
      console.error("Erreur lors de l'export Facebook:", err);
      setError("Une erreur est survenue lors de l'exportation.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Génère (ou renouvelle) l'URL de feed automatique pour Facebook.
   * Enregistre shopUrl et overrideFields côté serveur, liés au token.
   */
  const handleGenerateFeedUrl = async () => {
    if (!shopUrl) {
      setError("L'URL de base est requise pour générer le lien automatique.");
      return;
    }
    setFeedLoading(true);
    setError(null);
    try {
      const res = await facebookCatalogApi.generateToken({ shopUrl, overrideFields });
      setFeedUrl(res.data.feedUrl);
    } catch (err) {
      console.error('Erreur génération URL catalogue:', err);
      setError("Erreur lors de la génération de l'URL.");
    } finally {
      setFeedLoading(false);
    }
  };

  /** Copie l'URL dans le presse-papier */
  const handleCopyUrl = () => {
    navigator.clipboard.writeText(feedUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Fonction utilitaire pour échapper les champs CSV
  const escapeCsvField = (field) => {
    if (field === null || field === undefined) return '';
    const stringField = String(field);
    if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
      return `"${stringField.replace(/"/g, '""')}"`;
    }
    return stringField;
  };

  // Extraire une valeur d'option (Taille/Couleur)
  const extractOptionValue = (options, keys) => {
    if (!options) return '';
    // options peut être un objet { "Taille": "L" } ou un tableau [{name: "Taille", values: [...]}] selon le format
    // Ici on suppose un format clé-valeur simple comme { "Taille": "L", "Couleur": "Rose" }

    for (const key of keys) {
      // Recherche insensible à la casse
      const foundKey = Object.keys(options).find(k => k.toLowerCase() === key.toLowerCase());
      if (foundKey) return options[foundKey];
    }
    return '';
  };

  // Convertir les produits en format CSV Facebook
  const convertToFacebookCSV = (products, baseUrl, overrides) => {
    const headers = [
      'id',
      'title',
      'description',
      'availability',
      'condition',
      'price',
      'link',
      'image_link',
      'additional_image_link', // Champ ajouté pour les images IA supplémentaires
      'brand',
      'item_group_id',
      'google_product_category',
      'quantity_to_sell_on_facebook',
      'sale_price',
      'sale_price_effective_date',
      'gender',
      'color',
      'size',
      'age_group',
      'material',
      'pattern'
    ];

    const rows = [headers.join(',')];
    const cleanBaseUrl = baseUrl;
    // Filtrer les produits qui ont au moins une image générée par IA
    const productsWithAiImages = products.filter(product => {
      return product.generated_images && product.generated_images.length > 0;
    });

    productsWithAiImages.forEach(product => {
      const hasVariants = product.product_variants && product.product_variants.length > 0;
      const description = product.description || product.name;
      const brand = overrides.brand || "Ma Boutique";

      // Utiliser l'ID ou numeric_id s'il existe (fallback sur ID si numeric_id absent)
      // On convertit en nombre si possible pour le slug
      const productIdForSlug = product.numeric_id || product.id;

      // Extraire les images générées par IA
      const aiImages = product.generated_images || [];
      // image_link = première image IA
      const imageLink = aiImages.length > 0 ? aiImages[0].image_url : '';
      // additional_image_link = autres images IA séparées par des virgules
      const additionalImageLinks = aiImages.slice(1).map(img => img.image_url).join(',');

      if (hasVariants) {
        product.product_variants.forEach(variant => {
          const price = variant.price || 0;
          const comparePrice = variant.compare_at_price;
          const variantId = variant.id;

          // Titre : Nom Produit - Nom Variante
          const title = `${product.name}`;

          // Lien : format personnalisé {slug}-{numeric_id}
          // On ajoute le variant ID en query param si nécessaire, mais le client voulait surtout le format du path
          const productSlug = generateProductSlug(product.name, productIdForSlug);
          const link = cleanBaseUrl ? `${cleanBaseUrl}/${productSlug}` : '';

          // Utilisation des images IA au lieu des images de variantes
          const availability = (variant.inventory_quantity > 0) ? 'in stock' : 'out of stock';

          // Extraction auto des options
          const color = extractOptionValue(variant.options, ['couleur', 'color', 'colour']);
          const size = extractOptionValue(variant.options, ['taille', 'size']);

          const rowData = [
            escapeCsvField(variantId),
            escapeCsvField(title),
            escapeCsvField(description),
            escapeCsvField(availability),
            'new',
            escapeCsvField(`${price} ${getDeviseActive()}`),
            escapeCsvField(link),
            escapeCsvField(imageLink), // Première image IA
            escapeCsvField(additionalImageLinks), // Autres images IA
            escapeCsvField(brand),
            escapeCsvField(product.id),
            escapeCsvField(overrides.google_product_category),
            escapeCsvField(variant.inventory_quantity),
            comparePrice ? escapeCsvField(`${comparePrice} ${getDeviseActive()}`) : '',
            '', // sale_price_effective_date
            escapeCsvField(overrides.gender),
            escapeCsvField(color),
            escapeCsvField(size),
            escapeCsvField(overrides.age_group),
            '', // material
            ''  // pattern
          ];

          rows.push(rowData.join(','));
        });
      } else {
        const price = product.price || 0;
        const comparePrice = product.compare_at_price;
        const productSlug = generateProductSlug(product.name, productIdForSlug);
        const link = cleanBaseUrl ? `${cleanBaseUrl}/${productSlug}` : '';
        const stockLevel = product.stock_level || 0;
        const availability = (stockLevel > 0) ? 'in stock' : 'out of stock';

        const rowData = [
          escapeCsvField(product.id),
          escapeCsvField(product.name),
          escapeCsvField(description),
          escapeCsvField(availability),
          'new',
          escapeCsvField(`${price} ${getDeviseActive()}`),
          escapeCsvField(link),
          escapeCsvField(imageLink), // Première image IA
          escapeCsvField(additionalImageLinks), // Autres images IA
          escapeCsvField(brand),
          '',
          escapeCsvField(overrides.google_product_category),
          escapeCsvField(stockLevel),
          comparePrice ? escapeCsvField(`${comparePrice} ${getDeviseActive()}`) : '',
          '',
          escapeCsvField(overrides.gender),
          '', // color
          '', // size
          escapeCsvField(overrides.age_group),
          '', // material
          ''  // pattern
        ];
        rows.push(rowData.join(','));
      }
    });

    return rows.join('\n');
  };

  const downloadCSV = (csvContent, fileName) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="private-ui relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-start">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 flex items-center gap-2"
                  >
                    <ArrowDownTrayIcon className="h-6 w-6 text-primary-600" />
                    Exporter pour Facebook
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={onClose}
                  >
                    <span className="sr-only">Fermer</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Générez un fichier CSV compatible avec le Catalogue Facebook (Meta).
                  </p>
                </div>

                <form onSubmit={handleExport} className="mt-4 space-y-4">
                  <div>
                    <label htmlFor="shopUrl" className="block text-sm font-medium text-gray-700">
                      URL de base (ex: mikkoci.com/product-detail)
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="shopUrl"
                        id="shopUrl"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        placeholder="https://www.mikkoci.com/product-detail"
                        value={shopUrl}
                        onChange={(e) => setShopUrl(e.target.value)}
                        required
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Le lien final sera : <code>{shopUrl || '...'}/{'{slug}-{id}'}</code>
                    </p>
                  </div>

                  <div>
                    <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                      Filtrer par Tags (optionnel)
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="tags"
                        id="tags"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        placeholder="ex: Promos, Été"
                        value={tagsInput}
                        onChange={(e) => setTagsInput(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Options Avancées / Override Fields */}
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <button
                      type="button"
                      className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 focus:outline-none"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                    >
                      <span>Options Avancées (Valeurs par défaut)</span>
                      {showAdvanced ? (
                        <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                      )}
                    </button>

                    {showAdvanced && (
                      <div className="mt-3 grid grid-cols-1 gap-y-3 gap-x-4 sm:grid-cols-2 bg-gray-50 p-3 rounded-md">
                        <div>
                          <label htmlFor="gender" className="block text-xs font-medium text-gray-700">
                            Genre (Gender)
                          </label>
                          <select
                            id="gender"
                            className="mt-1 block w-full rounded-md border-gray-300 py-1.5 text-xs shadow-sm focus:border-primary-500 focus:ring-primary-500"
                            value={overrideFields.gender}
                            onChange={(e) => setOverrideFields({ ...overrideFields, gender: e.target.value })}
                          >
                            <option value="">-- Automatique --</option>
                            <option value="female">Femme (female)</option>
                            <option value="male">Homme (male)</option>
                            <option value="unisex">Unisexe (unisex)</option>
                          </select>
                        </div>

                        <div>
                          <label htmlFor="age_group" className="block text-xs font-medium text-gray-700">
                            Tranche d'âge
                          </label>
                          <select
                            id="age_group"
                            className="mt-1 block w-full rounded-md border-gray-300 py-1.5 text-xs shadow-sm focus:border-primary-500 focus:ring-primary-500"
                            value={overrideFields.age_group}
                            onChange={(e) => setOverrideFields({ ...overrideFields, age_group: e.target.value })}
                          >
                            <option value="">-- Automatique --</option>
                            <option value="adult">Adulte</option>
                            <option value="all ages">Tous âges</option>
                            <option value="kids">Enfant</option>
                            <option value="toddler">Bébé</option>
                            <option value="infant">Nourrisson</option>
                          </select>
                        </div>

                        <div>
                          <label htmlFor="brand" className="block text-xs font-medium text-gray-700">
                            Marque (Brand)
                          </label>
                          <input
                            type="text"
                            id="brand"
                            className="mt-1 block w-full rounded-md border-gray-300 py-1.5 text-xs shadow-sm focus:border-primary-500 focus:ring-primary-500"
                            placeholder="Ma Boutique"
                            value={overrideFields.brand}
                            onChange={(e) => setOverrideFields({ ...overrideFields, brand: e.target.value })}
                          />
                        </div>

                        <div>
                          <label htmlFor="category" className="block text-xs font-medium text-gray-700">
                            Catégorie Google
                          </label>
                          <input
                            type="text"
                            id="category"
                            className="mt-1 block w-full rounded-md border-gray-300 py-1.5 text-xs shadow-sm focus:border-primary-500 focus:ring-primary-500"
                            placeholder="ex: 1604 (Apparel...)"
                            value={overrideFields.google_product_category}
                            onChange={(e) => setOverrideFields({ ...overrideFields, google_product_category: e.target.value })}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Section URL automatique pour Facebook ── */}
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <LinkIcon className="h-4 w-4 text-primary-600" />
                      Lien automatique (Feed Facebook)
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Donnez ce lien à Facebook une seule fois. Il récupère vos produits automatiquement.
                    </p>

                    {/* Affichage de l'URL existante */}
                    {feedUrl && (
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          type="text"
                          readOnly
                          value={feedUrl}
                          className="block w-full rounded-md border-gray-300 bg-gray-50 text-xs shadow-sm focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={handleCopyUrl}
                          title="Copier l'URL"
                          className="flex-shrink-0 inline-flex items-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-500 shadow-sm hover:bg-gray-50"
                        >
                          {copied
                            ? <CheckIcon className="h-4 w-4 text-green-500" />
                            : <ClipboardDocumentIcon className="h-4 w-4" />
                          }
                        </button>
                      </div>
                    )}

                    {/* Bouton générer / renouveler */}
                    <button
                      type="button"
                      onClick={handleGenerateFeedUrl}
                      disabled={feedLoading || !shopUrl}
                      className="mt-2 inline-flex items-center gap-1 rounded-md border border-primary-300 bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700 hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <LinkIcon className="h-3.5 w-3.5" />
                      {feedLoading ? 'Génération...' : feedUrl ? 'Renouveler le lien' : 'Générer le lien automatique'}
                    </button>
                    {!shopUrl && (
                      <p className="mt-1 text-xs text-amber-600">Renseignez d'abord l'URL de base ci-dessus.</p>
                    )}
                  </div>

                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <div className="flex">
                        <div className="text-sm text-red-700">{error}</div>
                      </div>
                    </div>
                  )}

                  {success && (
                    <div className="rounded-md bg-green-50 p-4">
                      <div className="flex">
                        <div className="text-sm text-green-700">{success}</div>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                      onClick={onClose}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={loading || !shopUrl}
                      className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Exportation...
                        </>
                      ) : (
                        'Exporter CSV'
                      )}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default FacebookExportModal;
